import { parseISO, isSameDay } from 'date-fns';
import * as odataService from '@/core/common/services/odata.service';
import { compareAsc } from 'date-fns';

///// Helper Methode für den Flüge TAB auf der Reisetermin Detail und Releaseprüfung Seite /////

/**
 * Hilfsmethode für die Flugtypen
 * @param {Object} teilnehmer - Teilnehmer object
 * @param {String} flugstreckeTyp - Flugtyp (MainHin, MainRueck)
 * @return {Object|null} - Das passende Flugstreckenobjekt, oder null, wenn keines gefunden wird
 */
export function getFlugByType(teilnehmer, flugstreckeTyp) {
  if (!teilnehmer || !teilnehmer.teilnehmerReiseterminFlugstrecken) return null;
  return teilnehmer.teilnehmerReiseterminFlugstrecken.find(
    strecke => strecke.reiseterminFlugstrecke.flugstreckeTyp === flugstreckeTyp
  );
}

export function getHinflugForTeilnehmer(teilnehmer) {
  return getFlugByType(teilnehmer, 'MainHin');
}

export function getRueckflugForTeilnehmer(teilnehmer) {
  return getFlugByType(teilnehmer, 'MainRueck');
}

export function getInlandsflugeForTeilnehmer(teilnehmer) {
  if (!teilnehmer || !teilnehmer.teilnehmerReiseterminFlugstrecken) return [];
  return teilnehmer.teilnehmerReiseterminFlugstrecken
    .filter(
      teilnehmerReiseterminFlugstrecken =>
        teilnehmerReiseterminFlugstrecken.reiseterminFlugstrecke.flugstreckeTyp === 'Inlandsflug'
    )
    .sort((a, b) =>
      compareAsc(
        new Date(a.reiseterminFlugstrecke.abflugdatum),
        new Date(b.reiseterminFlugstrecke.abflugdatum)
      )
    );
}

/**
 * Hilfsmethode für die Verlängerungen
 * @param {Object} teilnehmer - Teilnehmer object
 * @param {Boolean} istVorverlaengerung - True für Vorverlaengerungen, false für reguläre Verlaengerungen
 * @returns {Array} - Array of Verlängerungen
 */
export function getVerlaengerungenByType(teilnehmer, istVorverlaengerung) {
  if (!teilnehmer || !teilnehmer.verlaengerungen) return [];
  return teilnehmer.verlaengerungen.filter(
    v => v.istVorverlaengerung === istVorverlaengerung && v.deletedAt == null
  );
}

export function getVerlaengerungenForTeilnehmer(teilnehmer) {
  return getVerlaengerungenByType(teilnehmer, false);
}

export function getVorverlaengerungenForTeilnehmer(teilnehmer) {
  return getVerlaengerungenByType(teilnehmer, true);
}

/**
 * Get Flüge für Blank Teilnehmer
 * @param {Object} teilnehmer - Teilnehmer object mit fluege
 * @param {Array} fluegeAfterBuchungReleased - Vorgang-weiter Vergleichsstand (vorgang.allFlights?.fluegeAfterBuchungReleased)
 * @returns {Array} - Array von Flügen
 */
function getFluegeForBlankTeilnehmer(teilnehmer, fluegeAfterBuchungReleased) {
  if (!teilnehmer || !teilnehmer.fluege) return [];
  return (
    teilnehmer.fluege
      // wirft Flüge raus, bei denen gleichzeitig flugstamm, ankunftsdatum und abflugdatum null sind
      .filter(flug => !(flug.flugstamm === null && flug.ankunftsdatum === null && flug.abflugdatum === null))
      .map(flug => {
        // Prüfe ob es alte Flugdaten nach Buchungsfreigabe gibt
        if (fluegeAfterBuchungReleased && fluegeAfterBuchungReleased.length > 0) {
          // Finde den entsprechenden alten Flug anhand der Flugnummer
          const oldFlug = fluegeAfterBuchungReleased.find(f => f.flugnummer === flug.flugnummer);
          if (
            oldFlug &&
            (oldFlug.abflugdatum !== flug.abflugdatum || oldFlug.ankunftsdatum !== flug.ankunftsdatum)
          ) {
            return {
              ...flug,
              oldFlugData: {
                abflughafen: oldFlug.abflughafen,
                ankunftsflughafen: oldFlug.ankunftsflughafen,
                abflugdatum: oldFlug.abflugdatum,
                ankunftsdatum: oldFlug.ankunftsdatum,
              },
            };
          }
        }
        return flug;
      })
  );
}

export function isArrivalTomorrow(item) {
  const departure = parseISO(item.abflugdatum);
  const arrival = parseISO(item.ankunftsdatum);
  return !isSameDay(arrival, departure);
}

/**
 * Gruppiert Teilnehmer eines Vorgangs nach identischem Flug-Set (PNR, Airline-PNR, Flugnummer, Daten, Häfen, Flugstamm)
 * @param {Object} vorgang - Vorgang object
 * @returns {Array} - Array von Gruppen: [{ key, teilnehmer, fluege }]
 */
export function groupBlankTeilnehmerByFluege(vorgang) {
  if (!vorgang || !vorgang.teilnehmer || vorgang.teilnehmer.length === 0) return [];

  // Sortierung nach Teilnehmernummer
  const sortedTeilnehmer = [...vorgang.teilnehmer].sort((a, b) => (a.nummer || 0) - (b.nummer || 0));

  // Map dient als Sammelstelle pro Flug-Kombination
  const groupsByGleicherFlugKombination = new Map();

  sortedTeilnehmer.forEach(teilnehmer => {
    // Pro Teilnehmer werden die ihm zugeordneten Flüge geholt und daraus ein Vergleichs-String gebildet
    const fluege = getFluegeForBlankTeilnehmer(teilnehmer, vorgang.allFlights?.fluegeAfterBuchungReleased);
    const flugKombination = buildFluegeKombination(fluege);

    if (!groupsByGleicherFlugKombination.has(flugKombination)) {
      // Beim ersten Teilnehmer einer Gruppe wird der Eintrag mit dessen Nummer als Key initialisiert
      groupsByGleicherFlugKombination.set(flugKombination, {
        key: teilnehmer.nummer,
        teilnehmer: [],
        fluege,
      });
    }
    groupsByGleicherFlugKombination.get(flugKombination).teilnehmer.push(teilnehmer);
  });

  return Array.from(groupsByGleicherFlugKombination.values());
}

/**
 * Erzeugt einen FlugKombinations-String für ein Flug-Set zum Gruppieren
 * @param {Array} fluege - Array von Flügen
 * @returns {String} - FlugKombination
 */
function buildFluegeKombination(fluege) {
  // Teilnehmer ohne zugeordnete Flüge (z.B. Eigenanreise/KeinFlug) bekommen einen eigenen, gemeinsamen Bucket
  if (!fluege || fluege.length === 0) return 'EMPTY';

  return fluege
    .map(f =>
      [
        f.pnr ?? '',
        f.airlinePnr ?? '',
        f.flugnummer ?? '',
        f.flugstamm ?? '',
        f.abflugdatum ?? '',
        f.ankunftsdatum ?? '',
        f.abflughafenEigen || f.abflughafen || '',
        f.ankunftsflughafenEigen || f.ankunftsflughafen || '',
      ].join('|')
    )
    .sort()
    .join('||');
}

export function getTeilnehmerKey(vorgangId, teilnehmerId) {
  return `${vorgangId}-${teilnehmerId}`;
}

/**
 * Öffnet oder schließt alle Vorgänge in einer liste UND alle Teilnehmer
 * @param {Object} context - Vue component context (this)
 * @param {Object} openState - BestimmerOpenState - zb. flugbuchungOpenState
 * @param {Array} vorgaenge - Array von Vorgängen
 * @param {Boolean} allOpen - true oder false
 * @param {Object} teilnehmerOpenState - TeilnehmerOpenState - zb. flugbuchungTeilnehmerOpenState
 * @param {String} teilnehmerField - Teilnehmer liste
 * @param {Function|null} getGroupKeys - Optional: liefert Gruppen-Keys pro Vorgang
 */
export function toggleAllVorgaengeUndTeilnehmer(
  context,
  openState,
  vorgaenge,
  allOpen,
  teilnehmerOpenState,
  teilnehmerField = 'teilnehmers',
  getGroupKeys = null
) {
  if (!vorgaenge || vorgaenge.length === 0) return;

  const newOpenState = !allOpen;

  // Beim Einklappen wird der innere State gelöscht statt auf false gesetzt, damit beim erneuten Öffnen einer Row die Auto-Expand-Logik für einzelne Inhalte wieder greift
  const setTeilnehmerOpenStateForKey = key => {
    if (newOpenState) {
      context.$set(teilnehmerOpenState, key, true);
    } else {
      context.$delete(teilnehmerOpenState, key);
    }
  };

  vorgaenge.forEach(vorgang => {
    // Toggle Vorgang
    context.$set(openState, vorgang.id, newOpenState);
    if (vorgang.anreiseart === 'KeinFlug') return;

    // Im Blank-Tab werden Teilnehmer nach Flügen gruppiert - dafür liefert die Komponente die Gruppen-Keys statt einer Teilnehmerliste
    if (getGroupKeys) {
      const keys = getGroupKeys(vorgang) || [];
      keys.forEach(k => {
        setTeilnehmerOpenStateForKey(getTeilnehmerKey(vorgang.id, k));
      });
      return;
    }

    // Toggle alle Teilnehmer in diesem Vorgang
    const teilnehmerList = vorgang[teilnehmerField];
    if (teilnehmerList && teilnehmerList.length > 0) {
      teilnehmerList.forEach((teilnehmer, index) => {
        const key = getTeilnehmerKey(
          vorgang.id,
          teilnehmerField === 'teilnehmers' ? teilnehmer.nummer || index : index
        );
        setTeilnehmerOpenStateForKey(key);
      });
    }
  });
}

export function toggleRow(context, item, openState) {
  context.$set(openState, item.id, !openState[item.id]);
}

/**
 * Läd Flugbuchungen aus dem BO FIle (aus dem Backend)
 * @param {Array} blankVorgaenge - Array von Blank vorgängen
 * @returns {Promise<Array>} - Promise - Array von vorgängen mit Flugdaten
 */
export async function loadFlugbuchungenAusBOFile(blankVorgaenge) {
  if (!blankVorgaenge || blankVorgaenge.length === 0) return [];

  const vorgangIds = blankVorgaenge.map(v => v.id);
  const response = await odataService.getVorgang({
    filter: { id: { in: vorgangIds } },
    expand: {
      hotelleistungen: {
        expand: {
          reisetermin: {
            select: ['thGesamt', 'thLangstrecke', 'thInlandsflug'],
          },
        },
      },
      flugbuchungen: {
        filter: { isDeleted: false },
        expand: {
          segmente: { filter: { isDeleted: false } },
          passagiere: { filter: { isDeleted: false } },
          tickets: {
            filter: { isDeleted: false },
            select: ['ticketNummer', 'passagierId'],
            expand: { segments: { select: ['segmentNummer'] } },
          },
        },
      },
      teilnehmers: {
        select: ['name', 'nummer', 'eigenanreiseart'],
        filter: { deletedAt: null },
        expand: {
          verlaengerungen: {
            filter: { deletedAt: null },
          },
          teilnehmerVerlaengerungen: {},
          teilnehmerReiseterminFlugstrecken: {
            expand: {
              segmente: {},
              reiseterminFlugstrecke: {},
            },
          },
        },
      },
    },
  });

  return response.data.map(vorgangWithFlugstrecken => {
    const segmentKeys = vorgangWithFlugstrecken.teilnehmers.flatMap(t =>
      t.teilnehmerReiseterminFlugstrecken.flatMap(strecke =>
        strecke.segmente.map(({ flugnummer, segmentNummer }) => `${flugnummer}_${segmentNummer}`)
      )
    );
    const uniqueSegmentKeys = new Set(segmentKeys);

    const nichtZugeordneteSegmente =
      vorgangWithFlugstrecken.flugbuchungen?.length > 0
        ? vorgangWithFlugstrecken.flugbuchungen
            .flatMap(f => f.segmente)
            .map(segment => ({
              ...segment,
              ticketReallyExists: vorgangWithFlugstrecken.flugbuchungen
                .filter(fb => fb.flugbuchungsId == segment.flugbuchungsId)
                .flatMap(f => f.tickets)
                .flatMap(t => t.segments)
                .map(seg => seg.segmentNummer)
                .some(nummer => nummer === segment.segmentNummer),
            }))
            .filter(s => !uniqueSegmentKeys.has(`${s.flugnummer}_${s.segmentNummer}`))
        : [];

    return {
      ...vorgangWithFlugstrecken,
      nichtZugeordneteSegmente,
      _showDetails: false,
    };
  });
}
